/**
 * Public Contact-Us page (Iter 67g).
 *
 * Single-column hero + two-column body: left side = soft "Get in touch" copy
 * with brand info chips (email / hours), right side = the form. Submission
 * posts to `POST /api/contact` — backend persists + emails the platform admin.
 *
 * Designed to slot into the existing colour language (sky → indigo gradient
 * accents, gradient submit button matching the Hero/Booking CTAs).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Sparkles, Clock, CheckCircle2 } from "lucide-react";

const SUPPORT_EMAIL = "hello@rentalcar.al";

export default function ContactUs() {
  usePageMeta("meta.contact.title", "meta.contact.description");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit = name.trim() && email.trim() && subject.trim() && message.trim() && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSent(true);
      toast.success("Thanks — we've received your message.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm mb-5">
            <Sparkles className="h-3 w-3" /> We're listening
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight font-medium" data-testid="contact-page-title">
            Get in touch.
          </h1>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            Questions about a booking, listing your fleet, or partnering with RentalCar.al?
            Drop us a note — we usually reply within one business day.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10">
          {/* Brand / contact info column */}
          <div className="space-y-4">
            <div className="rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-indigo-50/60 p-6">
              <div className="h-11 w-11 rounded-xl bg-white shadow-sm ring-1 ring-sky-100 flex items-center justify-center mb-4">
                <Mail className="h-5 w-5 text-sky-600" />
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">Email</div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-neutral-900 font-medium hover:text-sky-700"
                data-testid="contact-email-link"
              >
                {SUPPORT_EMAIL}
              </a>
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
                Booking issues, payouts, vendor onboarding, press, or anything else.
              </p>
            </div>

            <div className="rounded-3xl border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-rose-50/60 p-6">
              <div className="h-11 w-11 rounded-xl bg-white shadow-sm ring-1 ring-amber-100 flex items-center justify-center mb-4">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">Hours</div>
              <div className="text-neutral-900 font-medium">Mon — Fri · 09:00–18:00 CET</div>
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
                Out-of-hours messages are queued and answered the next business day.
              </p>
            </div>

            <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/60 p-6">
              <div className="h-11 w-11 rounded-xl bg-white shadow-sm ring-1 ring-violet-100 flex items-center justify-center mb-4">
                <MessageSquare className="h-5 w-5 text-violet-600" />
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">Already booked?</div>
              <p className="text-sm text-neutral-700 leading-relaxed">
                Your fastest route is the in-app host messaging from your booking detail page —
                replies land in your dashboard and the host's inbox at once.
              </p>
            </div>
          </div>

          {/* Form column */}
          <div className="rounded-3xl border border-border bg-white p-6 sm:p-8 shadow-sm">
            {sent ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="h-14 w-14 rounded-2xl bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <h2 className="font-display text-2xl font-medium mb-2">Message sent.</h2>
                <p className="text-neutral-600 max-w-md mx-auto">
                  Thanks {name.trim().split(" ")[0] || "there"} — we'll be in touch at{" "}
                  <strong>{email.trim()}</strong> shortly.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full mt-6"
                  onClick={() => {
                    setSent(false); setName(""); setEmail(""); setSubject(""); setMessage("");
                  }}
                  data-testid="contact-send-another"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-name" className="text-xs uppercase tracking-widest text-neutral-500">
                      Your name
                    </Label>
                    <Input
                      id="contact-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={120}
                      className="mt-1 rounded-xl"
                      placeholder="Ada Lovelace"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-xs uppercase tracking-widest text-neutral-500">
                      Email
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1 rounded-xl"
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-subject" className="text-xs uppercase tracking-widest text-neutral-500">
                    Subject
                  </Label>
                  <Input
                    id="contact-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    maxLength={200}
                    className="mt-1 rounded-xl"
                    placeholder="Question about your platform"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message" className="text-xs uppercase tracking-widest text-neutral-500">
                    Message
                  </Label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    minLength={1}
                    maxLength={5000}
                    rows={7}
                    className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 resize-y"
                    placeholder="Tell us a bit more…"
                    data-testid="contact-message-input"
                  />
                  <div className="text-[11px] text-neutral-400 mt-1 text-right">
                    {message.length} / 5000
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="rounded-full w-full h-12 bg-gradient-to-r from-sky-600 via-indigo-600 to-violet-600 hover:from-sky-700 hover:via-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? "Sending…" : "Send message"}
                </Button>
                <p className="text-[11px] text-neutral-500 text-center">
                  By submitting you agree to be contacted about your enquiry. We don't share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
